import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, ShoppingBag, TrendingUp, Euro, Plus, Edit, Trash2, 
  Eye, LogOut, Menu, X, Upload, Loader2, AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VendorDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [newProduct, setNewProduct] = useState({
    title: '',
    description: '',
    price: '',
    cost: '',
    stock: '',
    category: 'moda',
    images: []
  });

  const token = localStorage.getItem('qantri_vendor_token');

  useEffect(() => {
    // Check for PayPal return
    if (searchParams.get('success') === 'true') {
      const vendorId = searchParams.get('vendor_id');
      const paymentId = searchParams.get('paymentId');
      const payerId = searchParams.get('PayerID');
      
      if (vendorId && paymentId && payerId) {
        activateVendor(vendorId, paymentId, payerId);
      }
    } else if (token) {
      fetchData();
    } else {
      navigate('/sell');
    }
  }, []);

  const activateVendor = async (vendorId, paymentId, payerId) => {
    try {
      const res = await axios.post(`${API}/vendor/activate`, {
        vendor_id: vendorId,
        paymentId,
        PayerID: payerId
      });
      
      if (res.data.token) {
        localStorage.setItem('qantri_vendor_token', res.data.token);
        setVendor(res.data.vendor);
        toast.success('Pagamento confermato! Benvenuto su QANTRI');
        fetchData();
        // Clear URL params
        navigate('/vendor/dashboard', { replace: true });
      }
    } catch (error) {
      toast.error('Errore nell\'attivazione del pagamento');
      navigate('/sell');
    }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [vendorRes, dashboardRes, productsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/vendor/me`, { headers }),
        axios.get(`${API}/vendor/dashboard`, { headers }),
        axios.get(`${API}/vendor/products`, { headers }),
        axios.get(`${API}/vendor/orders`, { headers })
      ]);
      
      setVendor(vendorRes.data);
      setDashboard(dashboardRes.data);
      setProducts(productsRes.data.products);
      setOrders(ordersRes.data.orders);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('qantri_vendor_token');
        navigate('/sell');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('qantri_vendor_token');
    navigate('/sell');
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!newProduct.title || !newProduct.price) {
      toast.error('Compila almeno titolo e prezzo');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/vendor/products`, newProduct, { headers });
      toast.success('Prodotto aggiunto!');
      setShowAddProduct(false);
      setNewProduct({ title: '', description: '', price: '', cost: '', stock: '', category: 'moda', images: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta del prodotto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/vendor/products/${productId}`, { headers });
      toast.success('Prodotto eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProduct(prev => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b p-4 flex justify-between items-center sticky top-0 z-40">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu className="h-6 w-6" />
        </button>
        <span className="font-bold text-[#064E3B]">QANTRI Vendor</span>
        <button onClick={handleLogout} className="p-2 text-red-600">
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r transform transition-transform lg:transform-none flex flex-col h-screen lg:h-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <div className="p-6 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <span className="text-xl font-bold text-[#064E3B]">QANTRI</span>
              <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-neutral-500 mt-1">Dashboard Venditore</p>
          </div>
          
          <nav className="p-4 space-y-1 flex-grow overflow-y-auto">
            {[
              { id: 'overview', label: 'Panoramica', icon: TrendingUp },
              { id: 'products', label: 'Prodotti', icon: Package },
              { id: 'orders', label: 'Ordini', icon: ShoppingBag },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === item.id ? 'bg-[#064E3B] text-white' : 'hover:bg-neutral-100'}`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </button>
            ))}
          </nav>

          {/* User info section - fixed at bottom with proper spacing */}
          <div className="mt-auto p-4 border-t bg-white">
            <div className="mb-4">
              <p className="text-sm font-medium text-neutral-800 truncate">{vendor?.business_name}</p>
              <p className="text-xs text-neutral-500 truncate">{vendor?.email}</p>
              <span className={`inline-block mt-2 text-xs px-2 py-1 rounded-full ${vendor?.subscription_status === 'trial' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                {vendor?.subscription_status === 'trial' ? 'Periodo di prova' : vendor?.plan?.toUpperCase()}
              </span>
            </div>
            <button onClick={handleLogout} className="hidden lg:flex w-full items-center justify-center gap-2 text-red-600 hover:text-red-700 text-sm">
              <LogOut className="h-4 w-4" />
              Esci
            </button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {/* Payment Required Banner */}
          {dashboard?.payment_required && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-800">Pagamento richiesto</p>
                <p className="text-sm text-red-700 mb-3">Per caricare prodotti e iniziare a vendere, completa il pagamento del tuo abbonamento.</p>
                <Button 
                  onClick={() => navigate('/sell')}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Completa il pagamento
                </Button>
              </div>
            </div>
          )}

          {/* Trial Banner */}
          {dashboard?.in_trial && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-green-800">Periodo di prova attivo!</p>
                <p className="text-sm text-green-700">Stai vendendo senza commissioni. Dal prossimo mese si applicherà la commissione del {dashboard?.commission_rate}%.</p>
              </div>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold">Benvenuto, {vendor?.business_name}</h1>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Prodotti', value: dashboard?.products_count, max: dashboard?.max_products, icon: Package, color: 'bg-blue-500' },
                  { label: 'Ordini', value: dashboard?.total_orders, icon: ShoppingBag, color: 'bg-purple-500' },
                  { label: 'Vendite Totali', value: `€${dashboard?.total_sales?.toFixed(2)}`, icon: TrendingUp, color: 'bg-green-500' },
                  { label: 'Guadagno Netto', value: `€${dashboard?.net_earnings?.toFixed(2)}`, icon: Euro, color: 'bg-amber-500' },
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-xl p-4 shadow-sm"
                  >
                    <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                      <stat.icon className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-sm text-neutral-500">
                      {stat.label}
                      {stat.max && stat.max > 0 && <span className="text-xs"> / {stat.max}</span>}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="font-semibold mb-4">Azioni Rapide</h2>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => { setActiveTab('products'); setShowAddProduct(true); }} className="bg-[#064E3B]">
                    <Plus className="h-4 w-4 mr-2" />
                    Aggiungi Prodotto
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('orders')}>
                    <Eye className="h-4 w-4 mr-2" />
                    Vedi Ordini ({dashboard?.pending_orders} in attesa)
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">I Tuoi Prodotti</h1>
                <Button onClick={() => setShowAddProduct(true)} className="bg-[#064E3B]">
                  <Plus className="h-4 w-4 mr-2" />
                  Aggiungi
                </Button>
              </div>

              {products.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <Package className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-600 mb-4">Non hai ancora prodotti</p>
                  <Button onClick={() => setShowAddProduct(true)} className="bg-[#064E3B]">
                    Aggiungi il primo prodotto
                  </Button>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-neutral-50 border-b">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium">Prodotto</th>
                        <th className="text-left p-4 text-sm font-medium hidden md:table-cell">Categoria</th>
                        <th className="text-left p-4 text-sm font-medium">Prezzo</th>
                        <th className="text-left p-4 text-sm font-medium hidden md:table-cell">Stock</th>
                        <th className="text-right p-4 text-sm font-medium">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(product => (
                        <tr key={product.id} className="border-b hover:bg-neutral-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {product.images?.[0] ? (
                                <img src={product.images[0]} alt="" className="w-12 h-12 object-cover rounded" />
                              ) : (
                                <div className="w-12 h-12 bg-neutral-100 rounded flex items-center justify-center">
                                  <Package className="h-5 w-5 text-neutral-400" />
                                </div>
                              )}
                              <span className="font-medium line-clamp-1">{product.title?.it || product.original_title}</span>
                            </div>
                          </td>
                          <td className="p-4 hidden md:table-cell text-neutral-600">{product.category}</td>
                          <td className="p-4 font-medium">€{product.selling_price?.toFixed(2)}</td>
                          <td className="p-4 hidden md:table-cell">{product.stock_quantity}</td>
                          <td className="p-4 text-right">
                            <button onClick={() => handleDeleteProduct(product.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold">I Tuoi Ordini</h1>

              {orders.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <ShoppingBag className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-600">Nessun ordine ancora</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map(order => (
                    <div key={order.id} className="bg-white rounded-xl p-6 shadow-sm">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-medium">Ordine #{order.id?.slice(0, 8)}</p>
                          <p className="text-sm text-neutral-500">{new Date(order.created_at).toLocaleDateString('it-IT')}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          order.status === 'new' ? 'bg-blue-100 text-blue-700' :
                          order.status === 'shipped' ? 'bg-green-100 text-green-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {order.status === 'new' ? 'Nuovo' : order.status === 'shipped' ? 'Spedito' : order.status}
                        </span>
                      </div>
                      
                      <div className="border-t pt-4">
                        <p className="text-sm text-neutral-600 mb-2">
                          <strong>Cliente:</strong> {order.customer_name}<br />
                          <strong>Indirizzo:</strong> {order.shipping_address}, {order.shipping_city}
                        </p>
                        
                        <div className="mt-4 space-y-2">
                          {order.items?.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span>{item.title} x{item.quantity}</span>
                              <span className="font-medium">€{item.total?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-4 pt-4 border-t flex justify-between">
                          <span className="font-medium">Totale tuo</span>
                          <span className="font-bold text-[#064E3B]">€{order.vendor_total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Add Product Modal */}
      {showAddProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold">Aggiungi Prodotto</h3>
                <button onClick={() => setShowAddProduct(false)} className="p-2 hover:bg-neutral-100 rounded-full">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleAddProduct} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Titolo *</label>
                  <Input
                    value={newProduct.title}
                    onChange={(e) => setNewProduct({...newProduct, title: e.target.value})}
                    placeholder="Nome del prodotto"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Descrizione</label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    placeholder="Descrivi il prodotto..."
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Prezzo vendita (€) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                      placeholder="29.99"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Costo (€)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newProduct.cost}
                      onChange={(e) => setNewProduct({...newProduct, cost: e.target.value})}
                      placeholder="15.00"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Categoria</label>
                    <select
                      value={newProduct.category}
                      onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]"
                    >
                      <option value="moda">Moda</option>
                      <option value="elettronica">Elettronica</option>
                      <option value="casa">Casa</option>
                      <option value="sport">Sport</option>
                      <option value="bellezza">Bellezza</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Quantità</label>
                    <Input
                      type="number"
                      value={newProduct.stock}
                      onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})}
                      placeholder="10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Immagini</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {newProduct.images.map((img, i) => (
                      <div key={i} className="relative w-16 h-16">
                        <img src={img} alt="" className="w-full h-full object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => setNewProduct(prev => ({...prev, images: prev.images.filter((_, idx) => idx !== i)}))}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <label className="w-16 h-16 border-2 border-dashed border-neutral-300 rounded flex items-center justify-center cursor-pointer hover:border-[#064E3B]">
                      <Upload className="h-5 w-5 text-neutral-400" />
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    </label>
                  </div>
                  <p className="text-xs text-neutral-500">Oppure incolla URL immagine:</p>
                  <Input
                    placeholder="https://..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const url = e.target.value.trim();
                        if (url) {
                          setNewProduct(prev => ({...prev, images: [...prev.images, url]}));
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                </div>

                <Button type="submit" className="w-full bg-[#064E3B]">
                  Aggiungi Prodotto
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;
